import { themeSelector } from '@/redux/store/slices/theme/themeSlice'
import { themeOptionsMap, Theme } from '@/theme'
import { css, Global } from '@emotion/react'
import { createTheme, CssBaseline, ThemeProvider as MuiThemeProvider } from '@mui/material'
import { useAppSelector } from '@redux/hooks/useAppState'
import React, { useMemo, useEffect } from 'react'
import { presetColors } from '@/components/Workspace/WorkspaceNavigation/ColorToggle'

import type { FC } from 'react'

const GlobalCss = css`
	.firebase-emulator-warning {
		display: none;
	}

	@font-face {
		font-family: 'Telegraf';
		font-style: normal;
		font-weight: 300;
		font-display: swap;
		src: url(/fonts/PPTelegraf-Light.woff2) format('woff2');
	}
	@font-face {
		font-family: 'Telegraf';
		font-style: normal;
		font-weight: 400;
		font-display: swap;
		src: url(/fonts/PPTelegraf-Regular.woff2) format('woff2');
	}
	@font-face {
		font-family: 'Telegraf';
		font-style: normal;
		font-weight: 500;
		font-display: swap;
		src: url(/fonts/PPTelegraf-Medium.woff2) format('woff2');
	}
	@font-face {
		font-family: 'Telegraf';
		font-style: normal;
		font-weight: 700;
		font-display: swap;
		src: url(/fonts/PPTelegraf-Bold.woff2) format('woff2');
	}
`

export const ThemeProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
	const themeName = useAppSelector(themeSelector)
	const colorIndex = useAppSelector((state) => state.theme.colorIndex)
	
	const theme = useMemo(() => {
		const baseTheme = createTheme(themeOptionsMap[themeName as keyof typeof themeOptionsMap])
		const selectedColor = presetColors[colorIndex]
		
		// Override primary color in the theme
		return createTheme(baseTheme, {
			palette: {
				primary: {
					main: selectedColor.hex,
					light: selectedColor.hex,
					dark: selectedColor.hex,
					contrastText: '#FFFFFF',
				},
				action: {
					selected: `${selectedColor.hex}1F`,
					hover: `${selectedColor.hex}0A`,
				}
			}
		})
	}, [themeName, colorIndex])

	// Apply theme class to document for Tailwind/shadcn components
	useEffect(() => {
		const root = document.documentElement
		if (themeName === Theme.Dark) {
			root.classList.add('dark')
		} else {
			root.classList.remove('dark')
		}
	}, [themeName])

	return (
		<MuiThemeProvider theme={theme}>
			<Global styles={GlobalCss} />
			<CssBaseline />
			<>{children}</>
		</MuiThemeProvider>
	)
}
